import { Link } from 'wouter';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from 'recharts';
import { ArrowRight, Plug, Zap, Users, Send } from 'lucide-react';
import { trpc } from '../lib/trpc';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageSpinner,
  StatCard,
  Badge,
} from '../components/ui';

function greetingKey(): string {
  const h = new Date().getHours();
  if (h < 5) return 'overview.greetGoodNight';
  if (h < 12) return 'overview.greetGoodMorning';
  if (h < 18) return 'overview.greetGoodAfternoon';
  return 'overview.greetGoodEvening';
}

export function OverviewPage() {
  const { t } = useLocale();
  const { user } = useAuth();
  const stats = trpc.leads.stats.useQuery();
  const series = trpc.leads.getTimeSeries.useQuery({ days: 14 });
  const top = trpc.leads.getTopSources.useQuery({ days: 30, by: 'form' });
  const webhook = trpc.webhook.stats.useQuery();
  const conns = trpc.connections.list.useQuery();
  if (stats.isLoading) return <PageSpinner />;
  const s = stats.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">
          {t(greetingKey())}, {user?.name?.split(' ')[0] ?? t('overview.greetFallback')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('overview.subtitle')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('overview.leadsToday')}
          value={s?.leads.todayReceived ?? 0}
          sub={t('overview.yesterday', { count: s?.leads.yesterdayReceived ?? 0 })}
        />
        <StatCard
          label={t('overview.pendingDelivery')}
          value={s?.orders.pending ?? 0}
          sub={t('overview.inQueueForDelivery')}
        />
        <StatCard
          label={t('overview.sentToday')}
          value={s?.orders.sentToday ?? 0}
          sub={t('overview.allTime', { count: s?.orders.sent ?? 0 })}
        />
        <StatCard
          label={t('overview.failedToday')}
          value={s?.todayIntegrationLeads.leadsWithFailedDeliveryToday ?? 0}
          sub={t('overview.leadsWithFailedDelivery')}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('overview.volumeTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {series.data && series.data.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={series.data}>
                  <defs>
                    <linearGradient id="lv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d: string) => d.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="hsl(var(--primary))" fill="url(#lv)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-12 text-center text-sm text-muted-foreground">
                {t('overview.noDataPeriod')}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('overview.pipelineTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label={t('overview.totalLeads')} value={s?.leads.total ?? 0} />
            <Row label={t('overview.receivedDelivered')} value={s?.leads.received ?? 0} badge="success" />
            <Row label={t('overview.pending')} value={s?.leads.pending ?? 0} badge="warning" />
            <Row label={t('overview.failed')} value={s?.leads.failed ?? 0} badge="destructive" />
            <div className="border-t pt-3" />
            <Row label={t('overview.webhookEvents')} value={webhook.data?.total ?? 0} />
            <Row label={t('overview.processed')} value={webhook.data?.processed ?? 0} />
            <Row
              label={t('overview.invalidSignature')}
              value={webhook.data?.signatureInvalid ?? 0}
              badge={webhook.data?.signatureInvalid ? 'destructive' : undefined}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('overview.volumeByForm')}</CardTitle>
          </CardHeader>
          <CardContent>
            {top.data && top.data.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={top.data} layout="vertical" margin={{ left: 24 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={4} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-10 text-center text-sm text-muted-foreground">
                {t('overview.activityByForm')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('overview.quickAccess')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickLink
            href="/connections"
            icon={<Plug className="h-4 w-4" />}
            title={t('overview.connectFacebook')}
            desc={t('overview.accountCount', {
              count: conns.data?.filter((c) => c.type === 'facebook').length ?? 0,
            })}
          />
          <QuickLink
            href="/integrations"
            icon={<Zap className="h-4 w-4" />}
            title={t('overview.leadRouting')}
            desc={t('overview.connectFormsDest')}
          />
          <QuickLink
            href="/destinations"
            icon={<Send className="h-4 w-4" />}
            title={t('overview.destinations')}
            desc={t('overview.whereLeadsSent')}
          />
          <QuickLink
            href="/leads"
            icon={<Users className="h-4 w-4" />}
            title={t('overview.leadsTitle')}
            desc={t('overview.browseLeads')}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function Row({
  label,
  value,
  badge,
}: {
  label: string;
  value: React.ReactNode;
  badge?: 'success' | 'warning' | 'destructive';
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      {badge ? <Badge variant={badge}>{value}</Badge> : <span className="font-medium">{value}</span>}
    </div>
  );
}

function QuickLink({
  href,
  icon,
  title,
  desc,
}: {
  href: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <Link
      href={href}
      className="group flex items-center justify-between rounded-lg border p-4 transition-colors hover:border-primary/40 hover:bg-accent"
    >
      <div className="flex items-center gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">{icon}</div>
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}
